import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { LuDot } from "react-icons/lu";
import { useNavigate } from "react-router-dom";
import { IoIosArrowDown, IoMdArrowBack } from "react-icons/io";
import { MdDeleteSweep } from "react-icons/md";
import { RiDeleteBin6Line } from "react-icons/ri";
import { useDispatch, useSelector } from "react-redux";
import {
    accountHistory,
    deleteHistoryItem,
    clearAllHistory,
} from "../../redux/ApiSlice/AccountSettingSlice";

const ACTION_FILTERS = [
    { label: "All",                     value: "" },
    { label: "Account Login",           value: "login" },
    { label: "Account Logout",          value: "logout" },
    { label: "Password Changed",        value: "password_change" },
    { label: "Password Reset",          value: "password_reset" },
    { label: "Profile Updated",         value: "profile_update" },
    { label: "Email Changed",           value: "email_change" },
    { label: "Account Created",         value: "account_created" },
    { label: "Username Changed",        value: "username_change" },
    { label: "Name Changed",            value: "name_change" },
    { label: "About Us Changed",        value: "about_change" },
    { label: "Bio Changed",             value: "bio_change" },
    { label: "Profile Picture Changed", value: "avatar_change" },
];

const AccountHistory = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const {
        accountHistoryItems,
        accountHistoryMeta,
        accountHistoryLoading,
        accountHistoryLoadingMore,
        accountHistoryError,
        deleteHistoryLoading,
        clearHistoryLoading,
    } = useSelector((state) => state.AccountSetting);

    const [selectedSort, setSelectedSort] = useState("Recent");
    const [isSortOpen, setIsSortOpen] = useState(false);
    const [selectedFilter, setSelectedFilter] = useState(ACTION_FILTERS[0]);
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [deleteTargetId, setDeleteTargetId] = useState(null);
    const [showClearConfirm, setShowClearConfirm] = useState(false);
    const scrollRef = useRef(null);
    const sortRef = useRef(null);
    const filterRef = useRef(null);

    const fetchHistory = useCallback(
        (page = 1, append = false) => {
            dispatch(
                accountHistory({
                    page,
                    append,
                    action: selectedFilter.value,
                })
            );
        },
        [dispatch, selectedFilter.value]
    );

    useEffect(() => {
        fetchHistory(1, false);
    }, [fetchHistory]);

    useEffect(() => {
        const container = scrollRef.current;
        if (!container) return;
        const handleScroll = () => {
            const nearBottom =
                container.scrollTop + container.clientHeight >= container.scrollHeight - 200;
            if (nearBottom && accountHistoryMeta.next && !accountHistoryLoadingMore) {
                dispatch(
                    accountHistory({
                        url: accountHistoryMeta.next,
                        append: true,
                        action: selectedFilter.value,
                    })
                );
            }
        };
        container.addEventListener("scroll", handleScroll);
        return () => container.removeEventListener("scroll", handleScroll);
    }, [accountHistoryMeta.next, accountHistoryLoadingMore, dispatch, selectedFilter.value]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (sortRef.current && !sortRef.current.contains(e.target)) setIsSortOpen(false);
            if (filterRef.current && !filterRef.current.contains(e.target)) setIsFilterOpen(false);
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleFilterSelect = (filter) => {
        setSelectedFilter(filter);
        setIsFilterOpen(false);
    };

    const handleConfirmDelete = async () => {
        if (!deleteTargetId) return;
        try {
            await dispatch(deleteHistoryItem(deleteTargetId)).unwrap();
            setDeleteTargetId(null);
        } catch {
            // deleteHistoryError in Redux state; modal stays open so user can retry
        }
    };

    const handleConfirmClear = async () => {
        try {
            await dispatch(clearAllHistory()).unwrap();
            setShowClearConfirm(false);
        } catch {
            // clearHistoryError in Redux state; modal stays open so user can retry
        }
    };

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return "";
        const date = new Date(timestamp);
        const parts = new Intl.DateTimeFormat(undefined, {
            month: "short",
            day: "numeric",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
        }).formatToParts(date);
        const get = (type) => parts.find((p) => p.type === type)?.value || "";
        return `${get("month")} ${get("day")} ${get("year")}, ${get("hour")}:${get("minute")}`.trim();
    };

    const buildMessage = (item) => {
        if (item.change_description) return item.change_description;
        const device = [item.device_type, item.browser].filter(Boolean).join(" via ");
        const location = item.display || [item.city, item.state, item.country].filter(Boolean).join(", ");
        const osText = item.os ? ` on ${item.os}` : "";
        const locationText = location ? ` in ${location}` : "";
        const ipText = item.ip_address ? ` (IP ${item.ip_address})` : "";
        const verb = /login/i.test(item.action_display || "") ? "Signed in" : "Activity";
        if (!device && !location) return `${verb}${osText}${ipText}`.trim();
        return `${verb} from ${device || "your account"}${osText}${locationText}${ipText}`.trim();
    };

    const visibleItems = useMemo(() => {
        const sorted = [...accountHistoryItems].sort((a, b) => {
            const aTime = new Date(a.timestamp || 0).getTime();
            const bTime = new Date(b.timestamp || 0).getTime();
            return selectedSort === "Oldest" ? aTime - bTime : bTime - aTime;
        });
        return sorted;
    }, [accountHistoryItems, selectedSort]);

    return (
        <div ref={scrollRef} className="h-full bg-white dark:bg-[#1E1D21] dark:text-white overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 z-50">
                <div className="relative bg-[#BDE6FF] rounded-b-xl h-12 flex items-center justify-center">
                    <IoMdArrowBack
                        onClick={() => navigate(-1)}
                        className="absolute left-2 top-1/2 -translate-y-1/2 text-[22px] text-black cursor-pointer"
                    />
                    <p className="font-bold text-[16px] text-black">Account History</p>
                    {accountHistoryItems.length > 0 && (
                        <button
                            onClick={() => setShowClearConfirm(true)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1 text-[12px] text-black font-semibold"
                        >
                            <MdDeleteSweep className="text-[18px]" />
                            Clear All
                        </button>
                    )}
                </div>
            </div>

            {/* Sort + Filter dropdowns */}
            <div className="mt-4 px-2 flex w-full justify-between gap-2">
                {/* Sort */}
                <div ref={sortRef} className="relative w-[7.7rem]">
                    <button
                        onClick={() => { setIsSortOpen((o) => !o); setIsFilterOpen(false); }}
                        className="w-full px-2 py-1 rounded-md bg-white dark:bg-[#333333] dark:text-white border-2 border-[#BDE6FF] dark:border-gray-800/50 text-[14px] flex items-center justify-between gap-2"
                    >
                        {selectedSort}
                        <IoIosArrowDown className={`transition-transform ${isSortOpen ? "rotate-180" : ""}`} />
                    </button>
                    {isSortOpen && (
                        <div className="absolute left-0 mt-1 w-full bg-white dark:bg-[#333333] rounded-md shadow-lg z-10 overflow-hidden">
                            {["Recent", "Oldest"]
                                .filter((s) => s !== selectedSort)
                                .map((s) => (
                                    <div
                                        key={s}
                                        onClick={() => { setSelectedSort(s); setIsSortOpen(false); }}
                                        className="px-2 py-1 text-[14px] hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer text-gray-800 dark:text-gray-200"
                                    >
                                        {s}
                                    </div>
                                ))}
                        </div>
                    )}
                </div>

                {/* Filter */}
                <div ref={filterRef} className="relative w-[7.7rem]">
                    <button
                        onClick={() => { setIsFilterOpen((o) => !o); setIsSortOpen(false); }}
                        className="w-full px-2 py-1 rounded-md bg-white dark:bg-[#333333] dark:text-white border-2 border-[#BDE6FF] dark:border-gray-800/50 text-[14px] flex items-center justify-between gap-2"
                    >
                        <span className="truncate">{selectedFilter.label}</span>
                        <IoIosArrowDown className={`flex-shrink-0 transition-transform ${isFilterOpen ? "rotate-180" : ""}`} />
                    </button>
                    {isFilterOpen && (
                        <div className="absolute right-0 mt-1 w-[11rem] max-h-60 overflow-y-auto bg-white dark:bg-[#333333] rounded-md shadow-lg z-10">
                            {ACTION_FILTERS
                                .filter((f) => f.value !== selectedFilter.value)
                                .map((f) => (
                                    <div
                                        key={f.value}
                                        onClick={() => handleFilterSelect(f)}
                                        className="px-2 py-1 whitespace-nowrap text-[14px] hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer text-gray-800 dark:text-gray-200"
                                    >
                                        {f.label}
                                    </div>
                                ))}
                        </div>
                    )}
                </div>
            </div>

            {/* List */}
            <div className="mt-4">
                {accountHistoryError && (
                    <p className="px-3 text-[13px] text-red-600">{accountHistoryError}</p>
                )}

                {accountHistoryLoading && !visibleItems.length && (
                    <p className="px-3 text-[14px] text-gray-700 dark:text-gray-400">Loading activity…</p>
                )}

                {!accountHistoryLoading && !visibleItems.length && (
                    <p className="px-3 text-[14px] text-gray-700 dark:text-gray-400">No account history yet.</p>
                )}

                {visibleItems.map((item) => (
                    <div
                        key={item.id}
                        className="px-3 py-3 border-b border-gray-300 dark:border-gray-800/50 flex items-start gap-2"
                    >
                        <div className="flex-1 min-w-0">
                            <p className="text-[14px] font-bold text-black dark:text-white">
                                {formatTimestamp(item.timestamp)}
                            </p>
                            <div className="flex items-center mt-1 gap-1">
                                <LuDot className="text-[20px] text-black dark:text-white shrink-0" />
                                <p className="text-[14px] font-bold text-black dark:text-white flex-1">
                                    {item.action_display}
                                </p>
                                {item.is_success === false && (
                                    <span className="text-[12px] text-red-600 font-semibold">Failed</span>
                                )}
                            </div>
                            <p className="text-[12px] text-black dark:text-gray-300 mt-1 leading-5">
                                {buildMessage(item)}
                            </p>
                        </div>
                        <button
                            onClick={() => setDeleteTargetId(item.id)}
                            className="shrink-0 mt-1 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                            aria-label="Delete"
                        >
                            <RiDeleteBin6Line className="text-[16px]" />
                        </button>
                    </div>
                ))}

                {accountHistoryLoadingMore && (
                    <div className="flex justify-center py-4">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900 dark:border-white" />
                    </div>
                )}

                {!accountHistoryMeta.next && visibleItems.length > 0 && (
                    <p className="px-3 py-2 text-[12px] text-gray-500 text-center">
                        Showing all {visibleItems.length} {visibleItems.length === 1 ? "activity" : "activities"}.
                    </p>
                )}
            </div>

            {/* Delete single item modal */}
            {deleteTargetId && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200]"
                    onClick={() => setDeleteTargetId(null)}
                >
                    <div
                        className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-sm mx-4 w-full"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <p className="text-[20px] font-bold text-black dark:text-white text-center">
                            Delete this record?
                        </p>
                        <p className="text-[12px] text-gray-600 dark:text-gray-300 mt-1 text-center">
                            This action cannot be undone.
                        </p>
                        <div className="flex gap-3 mt-5 justify-center">
                            <button
                                onClick={() => setDeleteTargetId(null)}
                                className="px-4 py-1.5 rounded-md bg-gray-200 dark:bg-gray-600 text-black dark:text-white text-[14px]"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleConfirmDelete}
                                disabled={deleteHistoryLoading}
                                className="px-4 py-1.5 rounded-md bg-[#1b1b1b] hover:bg-black dark:bg-gray-400 dark:text-black text-white text-[14px] disabled:opacity-60"
                            >
                                {deleteHistoryLoading ? "Deleting…" : "Delete"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Clear all modal */}
            {showClearConfirm && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200]"
                    onClick={() => setShowClearConfirm(false)}
                >
                    <div
                        className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-sm mx-4 w-full"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <p className="text-[20px] font-bold text-black dark:text-white text-center">
                            Clear all history?
                        </p>
                        <p className="text-[12px] text-gray-600 dark:text-gray-300 mt-1 text-center">
                            All {accountHistoryMeta.count || visibleItems.length} records will be permanently deleted.
                        </p>
                        <div className="flex gap-3 mt-5 justify-center">
                            <button
                                onClick={() => setShowClearConfirm(false)}
                                className="px-4 py-1.5 rounded-md bg-gray-200 dark:bg-gray-600 text-black dark:text-white text-[14px]"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleConfirmClear}
                                disabled={clearHistoryLoading}
                                className="px-4 py-1.5 rounded-md bg-[#1b1b1b] hover:bg-black dark:bg-gray-400 dark:text-black text-white text-[14px] disabled:opacity-60"
                            >
                                {clearHistoryLoading ? "Clearing…" : "Clear All"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AccountHistory;
